import {
  cloneElement,
  Fragment,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type MouseEvent,
  type JSX,
} from 'react';
import { type CSSObject } from '@emotion/react';
import { type AriaProps } from '../../types';
import { type LinkSize, useLinkStyles } from './link.styles';
import { type ThemeNames } from '../../theme';
import { BodyS, CaptionLink, FontWeight } from '../../typography';
import { IconProps } from '@doxyme/icons';

const renderIcon = (
  icon: ReactNode,
  iconStyles: CSSObject,
): ReactNode | null => {
  if (!icon) return null;
  if (isValidElement(icon) && icon.type === Fragment) {
    return (icon as ReactElement<{ children?: ReactNode }>).props.children;
  }
  return cloneElement(icon as ReactElement<IconProps>, { css: iconStyles });
};

export type LinkProps = AriaProps & {
  id?: string;
  children: ReactNode | string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  size?: LinkSize;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  themeName?: ThemeNames;
  className?: string;
  fontWeight?: FontWeight;
  isDisabled?: boolean;
};

export const Link = ({
  id,
  children,
  startIcon,
  endIcon,
  size = 'regular',
  onClick,
  themeName,
  className,
  fontWeight,
  isDisabled = false,
  ...ariaProps
}: LinkProps): JSX.Element => {
  const styles = useLinkStyles({ themeName });
  const Element = size === 'regular' ? BodyS : CaptionLink;

  return (
    <button
      id={id}
      css={styles.container}
      className={className}
      type='button'
      onClick={onClick}
      disabled={isDisabled}
      {...ariaProps}
    >
      {renderIcon(startIcon, styles.icon)}
      <Element
        tag='span'
        css={styles.link}
        color='inherit'
        fontWeight={fontWeight}
      >
        {children}
      </Element>
      {renderIcon(endIcon, styles.icon)}
    </button>
  );
};
